import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple unsubscribe landing page linked from every lifecycle email.
// Posts the signed token to /api/email/unsubscribe on mount; shows a
// success message when the backend confirms the opt-out, or an error
// state if the token is invalid.
export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // 'loading' | 'ok' | 'error'

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState("error");
      return;
    }
    (async () => {
      try {
        await axios.post(`${API}/email/unsubscribe`, { token });
        if (!cancelled) setState("ok");
      } catch (_e) {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ backgroundColor: "#F7F4EE" }}
      data-testid="unsubscribe-page"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-8 text-center">
        {state === "loading" && (
          <p className="text-slate-600" data-testid="unsubscribe-loading">
            Updating your preferences…
          </p>
        )}
        {state === "ok" && (
          <>
            <h1 className="text-2xl font-semibold text-slate-800 mb-3" data-testid="unsubscribe-success">
              You're unsubscribed.
            </h1>
            <p className="text-slate-600 leading-relaxed">
              We won't send you any more of these emails. Your account, your progress, and
              your practices are all still here — this only stops the drip.
            </p>
            <p className="text-sm text-slate-500 mt-6">
              Changed your mind?{" "}
              <a
                href="mailto:mscwellness@protonmail.com"
                className="underline"
                data-testid="unsubscribe-reply-link"
              >
                Reply to any past email
              </a>{" "}
              and we'll turn them back on.
            </p>
            <Link
              to="/dashboard"
              className="inline-block mt-6 text-sm text-slate-700 underline"
              data-testid="unsubscribe-dashboard-link"
            >
              Back to dashboard
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="text-2xl font-semibold text-slate-800 mb-3" data-testid="unsubscribe-error">
              This link looks off.
            </h1>
            <p className="text-slate-600 leading-relaxed">
              We couldn't verify the unsubscribe link. If you'd like to stop these emails,
              reply to any past email from us and we'll take care of it.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
